import { Button, CircularProgress, Stack } from "@mui/material";
import { createElement, useState } from "react";
import { trackEvent } from "@/services/analytics-adapter";
import { useTranslation } from "react-i18next";
import FaceIconBrandLarge from "@/theme/icons/FaceIconBrandLarge";
import QuestionTypeSexAtBirth from "@/features/Common/components/QuestionTypeSexAtBirth";
import QuestionTypeWeight from "@/features/Common/components/QuestionTypeWeight";
import QuestionTypeDateOfBirth from "@/features/Common/components/QuestionTypeDateOfBirth";
import { Gender } from "../../../../types";
import CollectUserPersonalInformation from "../CollectUserPersonalInformation";
import { UserType } from "../../index";
import UserCreatedScreen from "../UserCreatedScreen";

interface PreFaceScanFlowProps {
  onComplete: Function;
  onHandlerNextQuestionType: Function;
  userType: UserType | null;
}

type QuestionTypeName =
  | "PersonalInformation"
  | "SexAtBirth"
  | "Weight"
  | "DateOfBirth";

interface QuestionTypesMetadata {
  [key: string]: {
    questionType: QuestionTypeName;
    component: any;
    onChange?: (value: any) => void;
    onCompletedFlow?: () => void;
  };
}

export default function PreFaceScanFlow({
  onComplete,
  onHandlerNextQuestionType,
  userType,
}: PreFaceScanFlowProps) {
  const stateNames: { [key: string]: QuestionTypeName } = {
    QUESTION_TYPE_PERSONAL_INFORMATION: "PersonalInformation",
    QUESTION_TYPE_SEX_AT_BIRTH: "SexAtBirth",
    QUESTION_TYPE_WEIGHT: "Weight",
    QUESTION_TYPE_DATE_OF_BIRTH: "DateOfBirth",
  };

  const { t } = useTranslation();

  const [currentQuestionType, setCurrentQuestionType] =
    useState<QuestionTypeName>(stateNames.QUESTION_TYPE_PERSONAL_INFORMATION);

  const [isValidInput, setIsValidInput] = useState(false);
  const [subjectDemographic, setSubjectDemographic] = useState<any>({});
  const [showUserCreatedScreen, setShowUserCreatedScreen] = useState(false);
  const [generatedUserId, setGeneratedUserId] = useState<string | null>(null);
  const [isCreatingTestUser, setIsCreatingTestUser] = useState(false);

  const questionTypesMapping: QuestionTypesMetadata = {
    PersonalInformation: {
      questionType: "PersonalInformation",
      component: CollectUserPersonalInformation,
      onChange: onPersonalInformationChange,
    },
    SexAtBirth: {
      questionType: "SexAtBirth",
      component: QuestionTypeSexAtBirth,
      onChange: onSexAtBirthChange,
    },
    Weight: {
      questionType: "Weight",
      component: QuestionTypeWeight,
      onChange: onWeightChange,
    },
    DateOfBirth: {
      questionType: "DateOfBirth",
      component: QuestionTypeDateOfBirth,
      onChange: onDateOfBirthChange,
    },
  };

  function getQuestionTypeComponent() {
    const { component, onChange, onCompletedFlow } =
      questionTypesMapping[currentQuestionType];

    const props: any = {
      onChange: onChange,
      onCompletedFlow: onCompletedFlow,
    };

    if (currentQuestionType === stateNames.QUESTION_TYPE_PERSONAL_INFORMATION) {
      props.userType = userType;
    }

    return createElement(component, props);
  }

  function onPersonalInformationChange(value: any) {
    setIsValidInput(value.isValid);

    if (value.isValid) {
      setSubjectDemographic({
        ...subjectDemographic,
        personalInformation: value.data,
      });

      setIsValidInput(value.isValid);
    }
  }

  function onSexAtBirthChange(value: any) {
    let gender = Gender.UNSPECIFIED;

    if (value.data.value) {
      gender = value.data.value;
      setSubjectDemographic({
        ...subjectDemographic,
        sexAtBirth: gender,
      });
      setIsValidInput(true);
    }
  }

  function onDateOfBirthChange(value: any) {
    if (value.isValid) {
      setSubjectDemographic({
        ...subjectDemographic,
        dateOfBirth: value.data.value,
      });
      setIsValidInput(true);
    }
  }

  function onWeightChange(value: any) {
    if (value.isValid) {
      setSubjectDemographic({
        ...subjectDemographic,
        weight: value.data.value,
      });
      setIsValidInput(true);
    }
  }

  const onContinue = async () => {
    setIsValidInput(false);

    trackEvent({
      event: "action.facePreScanContinue",
      source: "Public binah scan flow",
      questionType: currentQuestionType,
    });

    if (currentQuestionType === stateNames.QUESTION_TYPE_PERSONAL_INFORMATION) {
      if (userType === UserType.TEST_USER) {
        setIsCreatingTestUser(true);
        try {
          // TODO: Replace with actual API call
          const fakeApiResponse = await new Promise((resolve) =>
            setTimeout(() => resolve({ id: "TEST-12345-ABCDE" }), 1000)
          );
          const generatedIdResponse = (fakeApiResponse as { id: string }).id;
          setGeneratedUserId(generatedIdResponse);
          setSubjectDemographic({
            ...subjectDemographic,
            personalInformation: {
              ...subjectDemographic.personalInformation,
              policyNumber: generatedIdResponse,
            },
          });
          onHandlerNextQuestionType();
        } catch (error) {
          console.error("Failed to create test user:", error);
        } finally {
          setIsCreatingTestUser(false);
        }
        setShowUserCreatedScreen(true);
      } else {
        onHandlerNextQuestionType();
        setCurrentQuestionType(stateNames.QUESTION_TYPE_SEX_AT_BIRTH);
      }
    }

    if (currentQuestionType === stateNames.QUESTION_TYPE_SEX_AT_BIRTH) {
      onHandlerNextQuestionType();
      setCurrentQuestionType(stateNames.QUESTION_TYPE_WEIGHT);
    }

    if (currentQuestionType === stateNames.QUESTION_TYPE_WEIGHT) {
      onHandlerNextQuestionType();
      setCurrentQuestionType(stateNames.QUESTION_TYPE_DATE_OF_BIRTH);
    }

    if (currentQuestionType === stateNames.QUESTION_TYPE_DATE_OF_BIRTH) {
      onHandlerNextQuestionType();
      onComplete(subjectDemographic);
    }
  };

  function onUserCreatedContinue() {
    setShowUserCreatedScreen(false);
    setCurrentQuestionType(stateNames.QUESTION_TYPE_SEX_AT_BIRTH);
    onHandlerNextQuestionType();
  }

  if (showUserCreatedScreen) {
    return (
      <UserCreatedScreen
        onContinue={onUserCreatedContinue}
        generatedUserId={generatedUserId}
      />
    );
  }

  return (
    <Stack spacing={2} p={2} justifyContent="flex-end" sx={{ height: "100%" }}>
      <FaceIconBrandLarge />

      {getQuestionTypeComponent()}

      <Stack spacing={1} direction="row" sx={{ width: "100%" }}>
        <Button
          fullWidth
          disabled={!isValidInput || isCreatingTestUser}
          onClick={onContinue}
        >
          {isCreatingTestUser ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("common.nextButton")
          )}
        </Button>
      </Stack>
    </Stack>
  );
}
